import { defineComponent as _defineComponent } from 'vue';
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, withCtx as _withCtx, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = { class: "relative max-w-[850px]" };
const _hoisted_2 = { class: "flex list-none flex-wrap items-center gap-1.5 p-0" };
const _hoisted_3 = { class: "sr-only" };
const _hoisted_4 = { key: 0 };
const _hoisted_5 = ["aria-label"];
const _hoisted_6 = { class: "flex list-none flex-col gap-0.5 p-0" };
const _hoisted_7 = { class: "sr-only" };
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Popover } from "@eudonet/opal";
import { CONSENT_STATUS_THEME, DISPLAYED_STATUS_ICON, } from "@/modules/consent/domain/entities/consentOverview";
/**
 * The Figma spec for this line ("Badges line Spacing"):
 *  - the line is capped at 850px;
 *  - each badge truncates at 14 characters with an ellipsis (OPAL `Badge` `truncate` — the
 *    Notion Badges norm's overflow rule, tooltip included);
 *  - the last badge that no longer fits is replaced by a `[+N]` badge whose popover lists the
 *    remainder.
 *
 * Measuring is unavoidable: how many badges fit depends on their rendered text width, which only
 * the browser knows. The hidden row below exists for that — it always renders the FULL list so
 * widths stay measurable after the visible row has been truncated. Measuring the visible row
 * instead would be self-referential (hiding a badge changes the measurement that hid it).
 *
 * `purposes` is narrowed to {@link ConsentPurposeBadgeVm}, so a refusal cannot reach this line at
 * all: callers go through `toDisplayedPurposes`, and handing over a raw purpose list is a compile
 * error rather than a red badge the design never draws.
 */
const MAX_LINE_WIDTH = 850;
/** Figma: `gap-[var(--spacing/1․5,6px)]` between badges. */
const GAP = 6;
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConsentPurposeBadgeLine',
    props: {
        purposes: {}
    },
    setup(__props) {
        const props = __props;
        const { t } = useI18n();
        /** Figma: `max-w-[850px]` on the badge line group. */
        const measureRoot = ref(null);
        const visibleCount = ref(props.purposes.length);
        function recompute() {
            const root = measureRoot.value;
            if (!root)
                return;
            const widths = Array.from(root.children).map((child) => child.offsetWidth);
            if (widths.length === 0) {
                visibleCount.value = 0;
                return;
            }
            // The container can be narrower than the cap on a small viewport; the cap is a ceiling, not a
            // fixed width. `offsetWidth` of the measure row's parent is the real space available.
            const available = Math.min(root.parentElement?.offsetWidth || MAX_LINE_WIDTH, MAX_LINE_WIDTH);
            // Last element of the measure row is the +N probe, kept out of the badge widths.
            const overflowWidth = widths[widths.length - 1] ?? 0;
            const badgeWidths = widths.slice(0, -1);
            let used = 0;
            let fitted = 0;
            for (const width of badgeWidths) {
                const next = used + (fitted > 0 ? GAP : 0) + width;
                if (next > available)
                    break;
                used = next;
                fitted += 1;
            }
            if (fitted >= badgeWidths.length) {
                visibleCount.value = badgeWidths.length;
                return;
            }
            // Overflowing: the +N badge has to fit too, so give back badges until it does. Down to zero —
            // a single very long badge plus +N may not fit together, and showing +N alone still tells the
            // user there is more, whereas a clipped badge tells them nothing.
            // `used` already holds the width of the first `fitted` badges, so shrink it as we drop them
            // rather than re-summing the prefix on every pass.
            while (fitted > 0 && used + GAP + overflowWidth > available) {
                used -= badgeWidths[fitted - 1] + (fitted > 1 ? GAP : 0);
                fitted -= 1;
            }
            visibleCount.value = fitted;
        }
        const visiblePurposes = computed(() => props.purposes.slice(0, visibleCount.value));
        const hiddenPurposes = computed(() => props.purposes.slice(visibleCount.value));
        let observer = null;
        onMounted(() => {
            recompute();
            // Width changes with the viewport, the sidebar collapsing, or a font finishing loading — all of
            // which move the cut-off. Observing the parent covers every one without a resize listener.
            const target = measureRoot.value?.parentElement;
            if (target && typeof ResizeObserver !== "undefined") {
                observer = new ResizeObserver(() => recompute());
                observer.observe(target);
            }
        });
        onBeforeUnmount(() => {
            observer?.disconnect();
            observer = null;
        });
        // A new purpose list changes every width; wait for the hidden row to re-render before measuring it.
        // Shallow on purpose: the parent rebuilds the array on every recompute, and nothing mutates a
        // purpose object in place, so a deep traversal would only cost a walk it can never act on.
        watch(() => props.purposes, async () => {
            visibleCount.value = props.purposes.length;
            await nextTick();
            recompute();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", {
                    ref_key: "measureRoot",
                    ref: measureRoot,
                    class: "pointer-events-none invisible absolute left-0 top-0 flex gap-1.5 whitespace-nowrap",
                    "aria-hidden": "true"
                }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.purposes, (purpose) => {
                        return (_openBlock(), _createBlock(_unref(Badge), {
                            key: `measure-${purpose.code}`,
                            label: purpose.label,
                            theme: _unref(CONSENT_STATUS_THEME)[purpose.status],
                            size: "xs",
                            "show-close-icon": false,
                            truncate: "",
                            "left-icon": _unref(DISPLAYED_STATUS_ICON)[purpose.status]
                        }, null, 8 /* PROPS */, ["label", "theme", "left-icon"]));
                    }), 128 /* KEYED_FRAGMENT */)),
                    _createVNode(_unref(Badge), {
                        size: "xs",
                        theme: "indigo",
                        "text-only": "",
                        value: `+${__props.purposes.length}`
                    }, null, 8 /* PROPS */, ["value"])
                ], 512 /* NEED_PATCH */),
                _createElementVNode("ul", _hoisted_2, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePurposes.value, (purpose) => {
                        return (_openBlock(), _createElementBlock("li", {
                            key: purpose.code
                        }, [
                            _createVNode(_unref(Badge), {
                                label: purpose.label,
                                theme: _unref(CONSENT_STATUS_THEME)[purpose.status],
                                size: "xs",
                                "show-close-icon": false,
                                truncate: "",
                                "data-testid": "consent-purpose-badge",
                                "left-icon": _unref(DISPLAYED_STATUS_ICON)[purpose.status]
                            }, null, 8 /* PROPS */, ["label", "theme", "left-icon"]),
                            _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)(`contact.detail.consents.status.${purpose.status}`)), 1 /* TEXT */)
                        ]));
                    }), 128 /* KEYED_FRAGMENT */)),
                    (hiddenPurposes.value.length)
                        ? (_openBlock(), _createElementBlock("li", _hoisted_4, [
                            _createVNode(_unref(Popover), {
                                position: "top",
                                header: `+${hiddenPurposes.value.length}`
                            }, {
                                trigger: _withCtx(({ triggerProps }) => [
                                    _createElementVNode("button", _mergeProps(triggerProps, {
                                        type: "button",
                                        class: "inline-flex rounded-(--border-border-radius-rounded-xs) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--colors-text-accent)",
                                        "aria-label": _unref(t)('contact.detail.consents.morePurposes', { count: hiddenPurposes.value.length }),
                                        "data-testid": "consent-purpose-overflow-trigger"
                                    }), [
                                        _createVNode(_unref(Badge), {
                                            size: "xs",
                                            theme: "indigo",
                                            "text-only": "",
                                            value: `+${hiddenPurposes.value.length}`,
                                            "data-testid": "consent-purpose-overflow"
                                        }, null, 8 /* PROPS */, ["value"])
                                    ], 16 /* FULL_PROPS */, _hoisted_5)
                                ]),
                                default: _withCtx(() => [
                                    _createElementVNode("ul", _hoisted_6, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hiddenPurposes.value, (purpose) => {
                                            return (_openBlock(), _createElementBlock("li", {
                                                key: purpose.code,
                                                class: "flex items-center"
                                            }, [
                                                _createVNode(_unref(Badge), {
                                                    label: purpose.label,
                                                    theme: _unref(CONSENT_STATUS_THEME)[purpose.status],
                                                    size: "xs",
                                                    "show-close-icon": false,
                                                    truncate: "",
                                                    "data-testid": "consent-purpose-overflow-badge",
                                                    "left-icon": _unref(DISPLAYED_STATUS_ICON)[purpose.status]
                                                }, null, 8 /* PROPS */, ["label", "theme", "left-icon"]),
                                                _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)(`contact.detail.consents.status.${purpose.status}`)), 1 /* TEXT */)
                                            ]));
                                        }), 128 /* KEYED_FRAGMENT */))
                                    ])
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["header"])
                        ]))
                        : _createCommentVNode("v-if", true)
                ])
            ]));
        };
    }
});
